import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { ConfigService } from './config.service';

/** Risposta di worksite/categoria-info (ws_spring_cmp): suddivisione RFI/EDIL e HelpDesk RFI dell'azienda. */
export interface RfiInfo {
	enabled: boolean;
	forced: string | null;
	canChoose: boolean;
	ptIdReferenced: number[];
	currentPtId: number;
	/** HelpDesk RFI attivo per l'azienda: installazione con RFI (cmp.rfi.federation.enabled) e azienda con la suddivisione. */
	rfi: boolean;
	rfiPtIdEm: number;
	rfiPtIdLo: number;
}

const SENZA_RFI: RfiInfo = { enabled: false, forced: null, canChoose: false, ptIdReferenced: [], currentPtId: 0, rfi: false, rfiPtIdEm: 0, rfiPtIdLo: 0 };

/**
 * Modalita' RFI dell'azienda: dove l'HelpDesk RFI non c'e' (es. D'Adiutorio) le pagine comuni nascondono origine RFI,
 * voci e reparti RFI e non chiamano il servizio RFI. La modalita' di un cantiere la decide la sua categoria
 * (ws_categoria 'RFI' = stazione RFI; 'EDIL' o vuota = cantiere normale).
 */
@Injectable({ providedIn: 'root' })
export class RfiModeService {
	private cache = new Map<number, Observable<RfiInfo>>();

	constructor(
		private http: HttpClient,
		private config: ConfigService,
	) {}

	/** Informazioni dell'azienda (quella attiva se cmpId manca), lette una volta sola. In errore: nessun RFI. */
	info(cmpId?: number): Observable<RfiInfo> {
		const id = cmpId || this.config.getCompany_id();
		if (!id) return of(SENZA_RFI);
		let o = this.cache.get(id);
		if (!o) {
			o = this.http.get<RfiInfo>(`${this.config.companiesUrl}worksite/categoria-info?cmp_id=${id}`).pipe(
				map((r) => ({ ...SENZA_RFI, ...r, rfi: !!r?.rfi })),
				catchError(() => of(SENZA_RFI)),
				shareReplay(1),
			);
			this.cache.set(id, o);
		}
		return o;
	}

	/** HelpDesk RFI attivo per l'azienda. */
	attivo(cmpId?: number): Observable<boolean> {
		return this.info(cmpId).pipe(map((i) => i.rfi));
	}

	/** Il cantiere segue il flusso RFI (stazione RFI) solo se RFI e' attivo e la sua categoria e' RFI. */
	static isRfi(info: RfiInfo | null | undefined, ws: { ws_categoria?: string | null } | null | undefined): boolean {
		return !!info?.rfi && ws?.ws_categoria === 'RFI';
	}

	/** Portale della regione di un record RFI ('em' / 'lo'), 0 se l'origine non e' RFI. */
	static ptIdOrigine(info: RfiInfo, origin: string | null | undefined): number {
		return origin === 'em' ? info.rfiPtIdEm : origin === 'lo' ? info.rfiPtIdLo : 0;
	}
}
